import {computed, defineComponent, nextTick, reactive, ref, watch} from 'vue';
import {IDrawZoneMapGeometry} from '@/app/shared/components/kl-draw-zone-map/kl-draw-zone-map';
import {first} from 'lodash-es';
import KlDrawZoneMapUtils from '@/app/shared/components/kl-draw-zone-map/kl-draw-zone-map-utils';
import {IGeometry} from '@/api/klip-api.proxy';
import {useForm} from 'vee-validate';
import DateUtil from '@/app/shared/helpers/date-util';
import * as yup from 'yup';
import {toTypedSchema} from '@vee-validate/yup';
import {
    klValidationFields,
    ValidationFieldsType
} from '@/app/shared/components/kl-form-fields/kl-field-validation-util';


export interface IFlatGeometry {
    activationDate: string;
    version: string;
    precision: number;
    lastModificationDate: string;
    reference: string;
    wkt?: string;
}

export default defineComponent({
    compatConfig: {
        MODE: 3,
    },
    name: 'KlCreateGeometryOnMap',
    emits: ['revert', 'geometry-update'],
    props: {
        modRevertable: { type: Boolean, default: false },
        showActions: { type: Boolean, default: true },
    },
    setup(props, { emit }) {

        const schema = yup.object({
            activationDate: yup.array().kl_required_date().label('Activeringsdatum'),
            reference: yup.string().notRequired().max(100).label('Referentie'),
            version: yup.string().notRequired().max(100).label('Versie'),
            precision: yup.string().kl_number({ min: 0, max: 1000 }).notRequired().label('Nauwkeurigheid'),
            lastModificationDate: yup.array().kl_valid_date().label('Laatst bewerkt'),

            wkt: yup.string(), // OPT: validated manually during onSubmit because this can be a heavy backend call (otherwise validated with every change in a field value)
        });


        const geometryActivationMinDate = DateUtil.now().add(1, 'day').format('DD-MM-YYYY');
        const geometryLastModificationMaxDate = DateUtil.now().format('DD-MM-YYYY');

        const form = useForm({
            validationSchema: toTypedSchema(schema),
        });
        const fields: ValidationFieldsType<typeof schema> = klValidationFields<typeof schema>(schema);

        const disableSubmit = computed((): boolean => {
            return !form.meta.value.valid || !form.meta.value.dirty;
        });

        const activationDate = fields.activationDate.getValue<string[]>();
        const reference = fields.reference.getValue<string>();
        const version = fields.version.getValue<string>();
        const precision = fields.precision.getValue<number>();
        const lastModificationDate = fields.lastModificationDate.getValue<string[]>();
        const wkt = fields.wkt.getValue<string>();

        const geometryErrorMessages = ref<string[]>([]);
        const validatingGeometry = ref<boolean>(false);


        const flatGeometry = computed((): IFlatGeometry => ({
            activationDate: activationDate.value ? first(activationDate.value) : null,
            version: version.value,
            precision: precision.value,
            lastModificationDate: lastModificationDate.value ? first(lastModificationDate.value) : null,
            reference: reference.value,
            wkt: wkt.value,
        }));

        const _validateGeometry = async (): Promise<boolean> => {
            _clearGeometryErrors();

            validatingGeometry.value = true;

            // 'validate' can block the browser UI, so bump the heap to show loaders/waiting state
            await new Promise(r => setTimeout(r, 0));

            const rules = yup.object().kl_zoneGeometrySmallerThanXBytes(104857600).kl_zonePartiallyInFlanders();
            const validationResult = await KlDrawZoneMapUtils.validateWktGeometry(wkt.value, true, rules);

            geometryErrorMessages.value = validationResult.errors;

            validatingGeometry.value = false;
            return validationResult.valid;
        }

        const _submit = async (): Promise<boolean> => {

            if (!await _validateGeometry()) {
                return false;
            }

            emit('geometry-update', flatGeometry.value);

            return true;
        }

        const revert = () => {
            emit('revert');
        }

        const onNewGeometryChanged = (newGeometryZones: IDrawZoneMapGeometry[]) => {
            _clearGeometryErrors();
            wkt.value = first(newGeometryZones)?.wkt;
        }

        const _clearGeometryErrors = () => {
            geometryErrorMessages.value = [];
        }

        const _onInvalidSubmit = (submittedValues) => {
            console.log('_onInvalidSubmit', submittedValues);
        }

        const onSubmit = form.handleSubmit(_submit, _onInvalidSubmit);

        return {
            activationDate,
            reference,
            version,
            precision,
            lastModificationDate,

            geometryActivationMinDate,
            geometryLastModificationMaxDate,

            validatingGeometry,
            geometryErrorMessages,

            revert,
            onNewGeometryChanged,

            onSubmit,
            disableSubmit,

            fields,
        }
    }
})
